import mixpanel from 'mixpanel-browser';
import { useEffect } from 'react';

import { isProduction, mixpanelApiHost, mixpanelToken } from '@dashboard/services/env';

class FacebookPixel {
  constructor() {
    this.fbq = null;
    this.isInitialized = false;
  }

  init() {
    this.fbq = window.fbq;
    this.isInitialized = this.fbq && typeof this.fbq === 'function';
  }

  track(eventName, values = {}, eventId = '') {
    if (!this.isInitialized) {
      return;
    }

    this.fbq('track', eventName, values, { eventID: eventId });
  }
}

class GoogleTag {
  track(eventName, values = {}) {
    if (!window.gtag || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('event', eventName, values);
  }

  trackPageview() {
    if (!window.gtag || typeof window.gtag !== 'function') {
      return;
    }

    // Track in Google Analytics using the page_view event
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
    });

    // For Google Ads, we still use config to keep conversion tracking working
    window.gtag('config', 'AW-16833295745', {
      page_path: window.location.pathname + window.location.search,
      enhanced_conversions: true,
      allow_enhanced_conversions: true,
    });
  }
}

class LinkedInInsight {
  track(values = {}) {
    if (!window.lintrk || typeof window.lintrk !== 'function') {
      return;
    }

    window.lintrk('track', values);
  }
}

const facebookPixel = new FacebookPixel();
export const googleTag = new GoogleTag();
const linkedinInsight = new LinkedInInsight();

function checkShouldTrack(...args) {
  if (!isProduction) {
    console.log('Would track: ', ...args);
    return false;
  }

  if (!mixpanelToken) {
    return false;
  }

  return true;
}

function initTracking() {
  if (!checkShouldTrack('init')) {
    return;
  }

  mixpanel.init(mixpanelToken, {
    api_host: mixpanelApiHost,
    debug: process.env.NODE_ENV !== 'production',
    track_pageview: true,
    ip: true,
  });
  facebookPixel.init();
}

function useTracking() {
  useEffect(() => {
    initTracking();
  }, []);

  return null;
}

function trackEvent(eventName, values = {}) {
  if (!checkShouldTrack('trackEvent', eventName, values)) {
    return;
  }

  try {
    mixpanel.track(eventName, values);
  } catch (e) {
    // no-op
  }

  if (eventName === 'Signup') {
    const eventId = values.userId;

    facebookPixel.track('CompleteRegistration', {}, eventId);

    facebookPixel.track(
      'StartTrial',
      {
        value: values.predicted_ltv, // use full value for facebook pixel
        currency: values.currency,
      },
      eventId,
    );

    googleTag.track('sign_up', {
      method: 'email',
      currency: values.currency,
      transaction_id: eventId,
      ...(values.email && { user_data: { email_address: values.email } }),
    });

    googleTag.track('conversion', {
      send_to: 'AW-16833295745/0OjKCI-6_e4aEIHr3to-',
      value: values.predicted_ltv / 10, // use 10% of the value for google ads as we track full value on subscribe event
      currency: values.currency,
      transaction_id: eventId,
      ...(values.email && { user_data: { email_address: values.email } }),
    });

    linkedinInsight.track({
      conversion_id: 20555852,
    });
  }

  if (eventName === 'Subscribe') {
    const eventId = values.subscriptionId;

    // Send to Google Ads
    googleTag.track('conversion', {
      send_to: 'AW-16833295745/h9KqCKSLnJIaEIHr3to-',
      value: values.monthlyPrice,
      currency: values.currency,
      transaction_id: eventId,
      ...(values.email && { user_data: { email_address: values.email } }),
      ...(values.gclid && { gclid: values.gclid }),
    });

    // Also send as a purchase event for Google Analytics
    googleTag.track('purchase', {
      transaction_id: eventId,
      value: values.monthlyPrice,
      currency: values.currency,
      items: [
        {
          item_id: values.plan,
          item_name: values.planName || 'Subscription Plan',
          category: 'subscription',
          quantity: 1,
          price: values.monthlyPrice,
        },
      ],
    });

    facebookPixel.track(
      'Subscribe',
      {
        value: values.predicted_ltv,
        currency: values.currency,
        subscription_id: values.subscriptionId,
        plan_id: values.plan_id,
      },
      eventId,
    );

    linkedinInsight.track({
      conversion_id: 19718308,
    });
  }
}

function identifyUser(userId) {
  if (!checkShouldTrack('identifyUser', userId)) {
    return;
  }

  try {
    mixpanel.identify(userId);
  } catch (e) {
    // no-op
  }
}

function updateUser(values = {}) {
  if (!checkShouldTrack('updateUser', values)) {
    return;
  }

  try {
    mixpanel.people.set(values);
  } catch (e) {
    // no-op
  }
}

function setUser(values = {}) {
  if (!checkShouldTrack('setUser', values)) {
    return;
  }

  try {
    mixpanel.people.set_once(values);
  } catch (e) {
    // no-op
  }
}

function logout() {
  if (!checkShouldTrack('logout')) {
    return;
  }

  try {
    mixpanel.reset();
  } catch (e) {
    // no-op
  }
}

function trackPageview() {
  if (!checkShouldTrack('trackPageview')) {
    return;
  }

  try {
    mixpanel.track_pageview();
    googleTag.trackPageview();
  } catch (e) {
    // no-op
  }
}

const tracking = {
  identifyUser,
  trackEvent,
  setUser,
  updateUser,
  logout,
  trackPageview,
};

export { useTracking, tracking };
